/**
 * Item alterations (Foundry: rule-element/item-alteration/handlers.ts): a mode, a property and a
 * value applied to a copy of an Entry the Character holds, by the ItemAlteration Rule Element or
 * by the `alterations` of a GrantItem, Aura or EphemeralEffect. Every property Foundry handles is
 * handled here; anything that cannot be applied is reported against the Rule Element.
 */
import { ChangeFailure, getNewValue, type AELikeMode } from "./rule-elements/ae-like";
import type { RuleInstance } from "./rule-elements/base";
import { resolveValue } from "./resolve";
import type { EngineState } from "./state";
import { DAMAGE_DICE_FACES, MATERIAL_DAMAGE_EFFECTS, PHYSICAL_ITEM_TYPES, isDamageDieSize, isDamageType, nextDieSize, type DamageDieSize } from "./values";
import { addOrUpgradeTrait, removeTrait, upgradeWeaponTrait } from "./weapons";

export interface ItemAlteration {
  mode: AELikeMode;
  property: string;
  value: unknown;
  /** Whether the alteration comes from equipment (ABP, #12, ignores rune alterations from equipment). */
  fromEquipment?: boolean;
}

const MODES = ["add", "downgrade", "multiply", "override", "remove", "subtract", "upgrade"] as const;

/** Every property Foundry's ITEM_ALTERATION_HANDLERS knows. */
export const ITEM_ALTERATION_PROPERTIES = [
  "ac-bonus", "area-size", "badge-max", "badge-value", "bulk", "capacity", "category", "check-penalty", "damage-dice-faces",
  "damage-dice-number", "damage-type", "defense-passive", "description", "dex-cap", "focus-point-cost", "grade", "group",
  "hardness", "hp-max", "material-type", "pd-recovery-dc", "persistent-damage", "rarity", "range-increment", "range-max",
  "frequency-max", "frequency-per", "other-tags", "name", "runes-potency", "runes-resilient", "runes-striking", "speed-penalty",
  "strength", "traits", "level",
] as const;

/** Validate a raw `alterations` array. Returns the alterations, or a message for the first bad one. */
export function parseAlterations(raw: unknown): { alterations: ItemAlteration[]; error: string | null } {
  if (raw === undefined) return { alterations: [], error: null };
  if (!Array.isArray(raw)) return { alterations: [], error: "alterations: must be an array" };
  const alterations: ItemAlteration[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) return { alterations: [], error: "alterations: malformed entry" };
    const { mode, property, value, fromEquipment } = entry as Record<string, unknown>;
    if (!(MODES as readonly string[]).includes(String(mode))) return { alterations: [], error: `alterations: "${String(mode)}" is not a change mode` };
    if (typeof property !== "string" || !property) return { alterations: [], error: "alterations: property must be a non-empty string" };
    if (!(ITEM_ALTERATION_PROPERTIES as readonly string[]).includes(property)) return { alterations: [], error: `alterations: "${property}" is not an alterable property` };
    alterations.push({ mode: mode as AELikeMode, property, value, fromEquipment: fromEquipment !== false });
  }
  return { alterations, error: null };
}

/** What an alteration operates on: an EngineItem, an EngineWeapon, or a bare copy of an Entry. */
export interface AlterableItem {
  system: Record<string, unknown>;
  name: string;
  type: string;
  /** Badge value for a Condition or Effect (`badge-value`). */
  badge: { value: number } | null;
  /** A weapon's once-only die upgrade flag. */
  damageFacesUpgraded?: boolean;
  /** Human-readable record of the alterations applied, for the sheet. */
  applied?: string[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const FREQUENCIES = ["turn", "round", "PT1M", "PT10M", "PT1H", "PT24H", "day", "P1W", "P1M", "P1Y"] as const;
const FREQUENCY_SECONDS: Record<string, number> = { turn: 6, round: 6, PT1M: 60, PT10M: 600, PT1H: 3600, PT24H: 86400, day: 86400, P1W: 604800, P1M: 2592000, P1Y: 31536000 };
const RARITIES = ["common", "uncommon", "rare", "unique"];

function record(system: Record<string, unknown>, key: string): Record<string, unknown> {
  const existing = system[key];
  if (isRecord(existing)) return existing;
  const created: Record<string, unknown> = {};
  system[key] = created;
  return created;
}

function traitsOf(item: AlterableItem): { value: string[]; otherTags: string[]; config: Record<string, unknown> } & Record<string, unknown> {
  const traits = record(item.system, "traits");
  if (!Array.isArray(traits.value)) traits.value = [];
  if (!Array.isArray(traits.otherTags)) traits.otherTags = [];
  if (!isRecord(traits.config)) traits.config = {};
  return traits as { value: string[]; otherTags: string[]; config: Record<string, unknown> } & Record<string, unknown>;
}

/**
 * Apply one alteration to the copy. Returns a message when the property does not fit the item or
 * the change cannot be made; the caller records it against the producing Rule Element. An
 * alteration whose item type does not match is silently skipped (Foundry's `isValid` returns false).
 */
export function applyAlteration(rule: RuleInstance, state: EngineState, item: AlterableItem, alteration: ItemAlteration): string | null {
  const value = resolveValue(rule, state, alteration.value, null, { warn: false });
  const { mode, property } = alteration;
  const note = (text: string): null => {
    (item.applied ??= []).push(`${text} (${rule.item.name})`);
    return null;
  };
  const fail = (message: string): string => `alteration ${property}: ${message}`;
  const change = (current: unknown): unknown => getNewValue(mode, current, value);
  const numeric = (current: unknown): number | string => {
    if (typeof value !== "number") return fail("value must be a number");
    const next = change(typeof current === "number" ? current : 0);
    if (next instanceof ChangeFailure) return fail(next.message);
    return typeof next === "number" ? next : fail("could not compute a value");
  };
  const requireType = (...types: string[]): boolean => types.includes(item.type);
  const requireMode = (...modes: AELikeMode[]): string | null => (modes.includes(mode) ? null : fail(`mode "${mode}" is not allowed`));
  const abp = state.variantRules.automaticBonusProgression && alteration.fromEquipment !== false;

  switch (property) {
    case "ac-bonus": {
      if (!requireType("armor", "shield")) return null;
      const bad = requireMode("add", "downgrade", "override", "remove", "subtract", "upgrade");
      if (bad) return bad;
      const next = numeric(item.system.acBonus);
      if (typeof next === "string") return next;
      const runes = isRecord(item.system.runes) ? item.system.runes : {};
      const itemBonus = item.type === "armor" && mode === "override" && typeof runes.potency === "number" ? runes.potency : 0;
      item.system.acBonus = Math.max(next, 0) + itemBonus;
      return note(`AC bonus ${mode} ${String(value)}`);
    }
    case "area-size": {
      if (!requireType("spell")) return null;
      const bad = requireMode("add", "subtract", "upgrade", "downgrade", "override");
      if (bad) return bad;
      const area = item.system.area;
      if (!isRecord(area)) return null;
      const next = numeric(area.value);
      if (typeof next === "string") return next;
      area.value = Math.max(Math.floor(next / 5) * 5, 5);
      return note(`area ${mode} ${String(value)}`);
    }
    case "badge-max": {
      if (!requireType("effect")) return null;
      const bad = requireMode("downgrade", "override");
      if (bad) return bad;
      const badge = item.system.badge;
      if (!isRecord(badge) || badge.type !== "counter") return fail("effect lacks a counter badge");
      // Foundry fills a counter's max from its labels when the source leaves it null.
      const currentMax = typeof badge.max === "number" ? badge.max : Array.isArray(badge.labels) ? badge.labels.length : null;
      if (typeof badge.value !== "number" || currentMax === null) return null;
      const next = numeric(currentMax);
      if (typeof next === "string") return next;
      const hardMax = Array.isArray(badge.labels) ? badge.labels.length : next;
      const min = typeof badge.min === "number" ? badge.min : 0;
      const newMax = Math.min(Math.max(next, min), hardMax);
      badge.max = newMax;
      const newValue = Math.min(Math.max(badge.value, min), newMax) || 0;
      badge.value = newValue;
      if (item.badge) item.badge.value = newValue;
      return note(`badge max ${mode} ${String(value)}`);
    }
    case "badge-value": {
      if (!requireType("condition", "effect")) return null;
      const bad = requireMode("add", "downgrade", "override", "remove", "subtract", "upgrade");
      if (bad) return bad;
      const badge = item.type === "condition" ? item.system.value : item.system.badge;
      if (!isRecord(badge) || typeof badge.value !== "number") {
        if (item.badge) {
          const next = numeric(item.badge.value);
          if (typeof next === "string") return next;
          item.badge.value = Math.max(1, Math.trunc(next));
          return note(`badge ${mode} ${String(value)}`);
        }
        return fail("effect lacks a badge");
      }
      const current = item.badge?.value ?? badge.value;
      const next = numeric(current);
      if (typeof next === "string") return next;
      const max = typeof badge.max === "number" ? badge.max : Infinity;
      const min = typeof badge.min === "number" ? badge.min : 0;
      const newValue = Math.min(Math.max(next, min), max) || 0;
      badge.value = newValue;
      item.badge = { value: newValue };
      return note(`badge ${mode} ${String(value)}`);
    }
    case "bulk": {
      if (!requireType(...PHYSICAL_ITEM_TYPES)) return null;
      const bad = requireMode("override");
      if (bad) return bad;
      const bulk = Number(value) || 0;
      if (!(bulk === 0 || bulk === 0.1 || (Number.isInteger(bulk) && bulk >= 1 && bulk <= 100))) return fail("value must be 0, 0.1 or a whole number up to 100");
      record(item.system, "bulk").value = bulk;
      return note(`bulk ${bulk}`);
    }
    case "capacity": {
      if (!requireType("weapon")) return null;
      const ammo = item.system.ammo;
      if (!isRecord(ammo) || typeof ammo.capacity !== "number" || ammo.capacity < 1) return fail("the weapon does not use ammunition with a capacity");
      const next = numeric(ammo.capacity);
      if (typeof next === "string") return next;
      ammo.capacity = Math.max(next, 1);
      return note(`capacity ${mode} ${String(value)}`);
    }
    case "category": {
      if (!requireType("armor")) return null;
      const bad = requireMode("override");
      if (bad) return bad;
      if (!["light", "medium", "heavy"].includes(String(value))) return fail(`"${String(value)}" is not light, medium or heavy`);
      item.system.category = value;
      return note(`category ${String(value)}`);
    }
    case "check-penalty": {
      if (!requireType("armor")) return null;
      const next = numeric(item.system.checkPenalty);
      if (typeof next === "string") return next;
      item.system.checkPenalty = Math.min(next, 0);
      return note(`check penalty ${mode} ${String(value)}`);
    }
    case "damage-dice-faces": {
      if (!requireType("weapon")) return null;
      const damage = record(item.system, "damage");
      const die = damage.die;
      if (mode === "upgrade") {
        if (item.damageFacesUpgraded || !isDamageDieSize(die)) return null;
        damage.die = nextDieSize(die, "upgrade");
        item.damageFacesUpgraded = true;
      } else if (mode === "downgrade") {
        if (!isDamageDieSize(die)) return null;
        damage.die = nextDieSize(die, "downgrade");
      } else if (mode === "override") {
        if (typeof value !== "number" || !(DAMAGE_DICE_FACES as readonly number[]).includes(value)) return fail("value must be 4, 6, 8, 10 or 12 when overriding");
        if (isDamageDieSize(die) && value > Number(die.replace("d", ""))) item.damageFacesUpgraded = true;
        damage.die = `d${value}`;
      } else {
        return fail(`mode "${mode}" is not upgrade, downgrade or override`);
      }
      adjustTwoHandTrait(item, mode as "upgrade" | "downgrade" | "override");
      return note(`damage die ${mode}${mode === "override" ? ` d${String(value)}` : ""}`);
    }
    case "damage-dice-number": {
      if (!requireType("weapon")) return null;
      const damage = record(item.system, "damage");
      if (typeof damage.dice !== "number" || !damage.dice) return null;
      const next = numeric(damage.dice);
      if (typeof next === "string") return next;
      damage.dice = next;
      return note(`damage dice ${mode} ${String(value)}`);
    }
    case "damage-type": {
      if (!requireType("weapon")) return null;
      const bad = requireMode("override");
      if (bad) return bad;
      if (!isDamageType(value)) return fail(`"${String(value)}" is not a damage type`);
      record(item.system, "damage").damageType = value;
      return note(`damage type ${value}`);
    }
    case "defense-passive": {
      if (!requireType("spell")) return null;
      if (!["ac", "fortitude-dc", "reflex-dc", "will-dc"].includes(String(value))) return fail(`"${String(value)}" is not a passive defense`);
      const defense = item.system.defense;
      if (isRecord(defense) && isRecord(defense.passive)) defense.passive.statistic = value;
      return note(`targets ${String(value)}`);
    }
    case "description": {
      const bad = requireMode("add", "override");
      if (bad) return bad;
      const contents = (Array.isArray(value) ? value : []).filter(isRecord).map((v) => ({ title: typeof v.title === "string" ? v.title : null, text: String(v.text ?? ""), divider: v.divider === true, predicate: Array.isArray(v.predicate) ? v.predicate : [] }));
      if (contents.length === 0) return fail("value must be a list of description parts");
      const description = record(item.system, "description");
      if (mode === "override") description.override = contents;
      else (description.addenda as unknown[] | undefined)?.push({ label: rule.label, contents }) ?? (description.addenda = [{ label: rule.label, contents }]);
      return note(`description ${mode}`);
    }
    case "dex-cap": {
      if (!requireType("armor")) return null;
      const next = numeric(item.system.dexCap);
      if (typeof next === "string") return next;
      item.system.dexCap = Math.max(next, 0);
      return note(`Dex cap ${mode} ${String(value)}`);
    }
    case "focus-point-cost": {
      if (!requireType("spell")) return null;
      const bad = requireMode("add", "override", "upgrade");
      if (bad) return bad;
      const cast = record(item.system, "cast");
      const next = numeric(cast.focusPoints);
      if (typeof next === "string") return next;
      cast.focusPoints = Math.min(Math.max(next, 0), 3) || 0;
      return note(`focus point cost ${mode} ${String(value)}`);
    }
    case "grade":
      // Starfinder 2e item grades: Pathfinder Entries carry none, so there is nothing to change.
      return null;
    case "group": {
      if (!requireType("armor", "weapon")) return null;
      const bad = requireMode("override");
      if (bad) return bad;
      if (value !== null && typeof value !== "string") return fail("value must be a group slug or null");
      const table = state.catalogue?.table(item.type === "armor" ? "armorGroups" : "weaponGroups");
      if (typeof value === "string" && table && !(value in table)) return fail(`"${value}" is not a ${item.type} group`);
      item.system.group = value;
      return note(`group ${String(value)}`);
    }
    case "hardness": {
      if (!requireType(...PHYSICAL_ITEM_TYPES)) return null;
      if (typeof value !== "number" || value < -100 || value > 100) return fail("value must be a number between -100 and 100");
      const next = numeric(item.system.hardness);
      if (typeof next === "string") return next;
      item.system.hardness = Math.max(Math.trunc(next), 0);
      return note(`hardness ${mode} ${String(value)}`);
    }
    case "hp-max": {
      if (!requireType(...PHYSICAL_ITEM_TYPES)) return null;
      if (typeof value !== "number" || value <= 0) return fail("value must be a positive number");
      const hp = record(item.system, "hp");
      const next = numeric(hp.max);
      if (typeof next === "string") return next;
      const newMax = Math.max(Math.trunc(next), 1);
      hp.max = newMax;
      if ("brokenThreshold" in hp) hp.brokenThreshold = Math.floor(newMax / 2);
      return note(`max HP ${mode} ${String(value)}`);
    }
    case "material-type": {
      if (!requireType(...PHYSICAL_ITEM_TYPES)) return null;
      const bad = requireMode("override");
      if (bad) return bad;
      const table = state.catalogue?.table("preciousMaterials");
      const known = table ? String(value) in table : (MATERIAL_DAMAGE_EFFECTS as readonly string[]).includes(String(value));
      if (typeof value !== "string" || !known) return fail(`"${String(value)}" is not a precious material`);
      item.system.material = { type: value, grade: "standard" };
      return note(`material ${value}`);
    }
    case "pd-recovery-dc": {
      if (!requireType("condition")) return null;
      if (item.system.slug !== "persistent-damage") return fail("item must be a persistent damage condition");
      const persistent = item.system.persistent;
      if (!isRecord(persistent)) return null;
      const next = numeric(persistent.dc);
      if (typeof next === "string") return next;
      persistent.dc = Math.max(next, 0);
      return note(`recovery DC ${mode} ${String(value)}`);
    }
    case "persistent-damage": {
      if (!requireType("condition")) return null;
      if (item.system.slug !== "persistent-damage") return fail("item must be a persistent damage condition");
      const bad = requireMode("override");
      if (bad) return bad;
      if (!isRecord(value)) return fail("value must be an object with formula, damageType and dc");
      const dc = Math.trunc(Math.abs(Number(value.dc) || 15));
      item.system.persistent = { formula: String(value.formula ?? ""), damageType: String(value.damageType ?? "untyped"), dc, criticalHit: value.criticalHit === true };
      return note(`persistent damage ${String(value.formula)} ${String(value.damageType)}`);
    }
    case "rarity": {
      const bad = requireMode("override");
      if (bad) return bad;
      if (typeof value !== "string" || !RARITIES.includes(value)) return fail(`"${String(value)}" is not a rarity`);
      traitsOf(item).rarity = value;
      return note(`rarity ${value}`);
    }
    case "range-increment":
    case "range-max": {
      if (!requireType("weapon")) return null;
      const bad = requireMode("add", "multiply", "override", "remove", "subtract");
      if (bad) return bad;
      const key = property === "range-increment" ? "range" : "maxRange";
      const current = item.system[key];
      if (typeof current !== "number" || !current) return null;
      const next = numeric(current);
      if (typeof next === "string") return next;
      item.system[key] = next;
      return note(`${property.replace("-", " ")} ${mode} ${String(value)}`);
    }
    case "frequency-max": {
      if (!requireType("action", "feat")) return null;
      if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) return fail("value must be a positive whole number");
      const frequency = isRecord(item.system.frequency) ? item.system.frequency : (item.system.frequency = { value: undefined, max: 1, per: "day" });
      const next = numeric(frequency.max);
      if (typeof next === "string") return next;
      frequency.max = next;
      frequency.value = Math.min(Math.max(typeof frequency.value === "number" ? frequency.value : next, 0), next);
      return note(`frequency ${mode} ${String(value)}`);
    }
    case "frequency-per": {
      if (!requireType("action", "feat")) return null;
      const bad = requireMode("downgrade", "override", "upgrade");
      if (bad) return bad;
      if (typeof value !== "string" || !(FREQUENCIES as readonly string[]).includes(value)) return fail(`"${String(value)}" is not a frequency interval`);
      const frequency = isRecord(item.system.frequency) ? item.system.frequency : (item.system.frequency = { value: undefined, max: 1, per: "day" });
      const current = String(frequency.per ?? "day");
      const newIsLonger = (value === "round" && current === "turn") || (value === "PT24H" && current === "day") || (FREQUENCY_SECONDS[value] ?? 0) > (FREQUENCY_SECONDS[current] ?? 0);
      if (mode === "override" || (mode === "upgrade" && newIsLonger) || (mode === "downgrade" && !newIsLonger)) frequency.per = value;
      return note(`frequency per ${value}`);
    }
    case "other-tags": {
      const bad = requireMode("add", "subtract", "remove");
      if (bad) return bad;
      if (typeof value !== "string" || !value) return fail("value must be a slug");
      const tags = traitsOf(item).otherTags;
      if (mode === "add") {
        if (!tags.includes(value)) tags.push(value);
      } else {
        const index = tags.indexOf(value);
        if (index !== -1) tags.splice(index, 1);
      }
      return note(`tag ${mode === "add" ? "+" : "-"}${value}`);
    }
    case "name": {
      const bad = requireMode("override");
      if (bad) return bad;
      if (typeof value !== "string" || !value) return fail("value must be a non-empty string");
      item.name = state.catalogue?.localize(value) ?? value;
      return note(`name ${item.name}`);
    }
    case "runes-potency": {
      if (!requireType("weapon", "armor")) return null;
      const bad = requireMode("upgrade", "override");
      if (bad) return bad;
      if (abp) return null;
      if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > 4) return fail("value must be a whole number from 0 to 4");
      const runes = record(item.system, "runes");
      const previous = typeof runes.potency === "number" ? runes.potency : 0;
      const next = numeric(previous);
      if (typeof next === "string") return next;
      const potency = Math.min(Math.max(next, 0), 4);
      runes.potency = potency;
      if (potency !== previous) {
        const traits = traitsOf(item);
        if (potency > previous && !traits.value.some((t) => t === "magical" || ["arcane", "divine", "occult", "primal"].includes(t))) traits.value.push("magical");
        if (item.type === "armor" && typeof item.system.acBonus === "number") item.system.acBonus += potency - previous;
      }
      return note(`potency rune ${mode} ${String(value)}`);
    }
    case "runes-resilient": {
      if (!requireType("armor")) return null;
      const bad = requireMode("upgrade", "override");
      if (bad) return bad;
      if (abp) return null;
      if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > 4) return fail("value must be a whole number from 0 to 4");
      const runes = record(item.system, "runes");
      const next = numeric(typeof runes.resilient === "number" ? runes.resilient : 0);
      if (typeof next === "string") return next;
      runes.resilient = Math.min(Math.max(next, 0), 4);
      return note(`resilient rune ${mode} ${String(value)}`);
    }
    case "runes-striking": {
      if (!requireType("weapon")) return null;
      const bad = requireMode("upgrade", "override");
      if (bad) return bad;
      if (abp) return null;
      if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > 4) return fail("value must be a whole number from 0 to 4");
      const runes = record(item.system, "runes");
      const previous = typeof runes.striking === "number" ? runes.striking : 0;
      const next = numeric(previous);
      if (typeof next === "string") return next;
      const striking = Math.min(Math.max(next, 0), 4);
      runes.striking = striking;
      if (striking !== previous) record(item.system, "damage").dice = 1 + striking;
      return note(`striking rune ${mode} ${String(value)}`);
    }
    case "speed-penalty": {
      if (!requireType("armor", "shield")) return null;
      const next = numeric(item.system.speedPenalty);
      if (typeof next === "string") return next;
      item.system.speedPenalty = Math.min(next, 0);
      return note(`speed penalty ${mode} ${String(value)}`);
    }
    case "strength": {
      if (!requireType("armor")) return null;
      if (item.system.strength === null || item.system.strength === undefined) return null;
      if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) return fail("value must be a positive whole number");
      const next = numeric(item.system.strength);
      if (typeof next === "string") return next;
      item.system.strength = Math.max(next, -2);
      return note(`Strength ${mode} ${String(value)}`);
    }
    case "traits": {
      const bad = requireMode("add", "remove", "subtract");
      if (bad) return bad;
      if (!isRecord(item.system.traits) && !["feat", "action", "spell", "weapon", "armor", "shield", "equipment", "consumable", "backpack", "effect", "condition", "ancestry", "heritage", "background", "class", "treasure", "book", "affliction", "campaignFeature"].includes(item.type)) return null;
      let resolved: string;
      if (isRecord(value)) {
        if (mode !== "add") return fail(`value cannot be an object when mode is ${mode}`);
        resolved = `${String(value.trait)}-${String(resolveValue(rule, state, value.annotation, null, { warn: false }))}`;
      } else if (typeof value === "string" && value) {
        resolved = value;
      } else {
        return fail("value must be a trait");
      }
      const traits = traitsOf(item);
      if (mode === "add") {
        addOrUpgradeTrait(traits.value, resolved);
        if (item.type === "weapon" && /^thrown-\d{1,3}$/.test(resolved) && typeof item.system.range === "number") item.system.range = null;
        if (item.type === "weapon" && resolved === "modular") {
          const modular = (Array.isArray(traits.config.modular) ? traits.config.modular : (traits.config.modular = [])) as { damageType: string; traits: string[] }[];
          for (const c of [{ damageType: "bludgeoning", traits: [] }, { damageType: "piercing", traits: [] }, { damageType: "slashing", traits: [] }]) {
            if (!modular.some((m) => JSON.stringify(m) === JSON.stringify(c))) modular.push(c);
          }
        }
      } else {
        removeTrait(traits.value, resolved);
      }
      return note(`trait ${mode === "add" ? "+" : "-"}${resolved}`);
    }
    case "level": {
      const level = record(item.system, "level");
      const next = numeric(level.value);
      if (typeof next === "string") return next;
      level.value = next;
      return note(`level ${mode} ${String(value)}`);
    }
    default:
      return fail("is not an alterable property");
  }
}

/** Keep a `two-hand-dN` trait in step with a changed base die (Foundry: adjustTwoHandTraitForDamageFacesChange). */
function adjustTwoHandTrait(item: AlterableItem, mode: "upgrade" | "downgrade" | "override"): void {
  const traits = traitsOf(item);
  const index = traits.value.findIndex((t) => t.startsWith("two-hand-d"));
  if (index < 0) return;
  const twoHand = traits.value[index]!;
  const faces = Number(twoHand.replace("two-hand-d", ""));
  let next = twoHand;
  if (mode === "upgrade") next = upgradeWeaponTrait(twoHand);
  else if (mode === "downgrade") {
    if ((DAMAGE_DICE_FACES as readonly number[]).includes(faces)) next = `two-hand-${nextDieSize(`d${faces}` as DamageDieSize, "downgrade")}`;
  } else {
    const die = (item.system.damage as { die?: unknown } | undefined)?.die;
    if (isDamageDieSize(die)) {
      const dieFaces = Number(die.replace("d", ""));
      if (faces <= dieFaces) {
        const upgraded = Number(nextDieSize(die, "upgrade").replace("d", ""));
        next = upgraded > dieFaces ? `two-hand-d${upgraded}` : twoHand;
      }
    }
  }
  if (next !== twoHand) traits.value.splice(index, 1, next);
}
